import { useMemo } from 'react';
import { useAppSelector } from '../redux/hooks';
import { useGetComponentListQuery, type ComponentListItem } from '../redux/api/entityApi';

/**
 * Builds a map from component primary_id → STC power in watts.
 * Uses the lightweight getComponentList endpoint (properties only).
 */
export function useComponentPowerMap(): {
  powerMap: Map<string, number>;
  isLoading: boolean;
} {
  const currentSite = useAppSelector((state) => state.site.currentSite);
  const portfolioId = useAppSelector((state) => state.portfolio.selectedPortfolioId);
  const siteId = currentSite?.site_id || currentSite?.id || '';

  const { data, isLoading } = useGetComponentListQuery(
    { portfolio_id: portfolioId!, site_id: siteId, exclude_modules: true },
    { skip: !portfolioId || !siteId }
  );

  const powerMap = useMemo(() => {
    const map = new Map<string, number>();
    if (!data?.items) return map;

    for (const comp of data.items) {
      const power = extractPowerW(comp);
      if (power != null && power > 0) {
        map.set(comp.primary_id, power);
      }
    }

    return map;
  }, [data]);

  return { powerMap, isLoading };
}

function extractPowerW(comp: ComponentListItem): number | null {
  const props = comp.properties;
  if (!props) return null;

  // Check common power field names
  const candidates = [
    props['STC Power (W)'],
    props['stc_power_w'],
    props['Power (W)'],
    props['power_w'],
    props['Rated Power (W)'],
    props['rated_power_w'],
    props['Power'],
  ];

  for (const val of candidates) {
    if (val != null) {
      const num = Number(val);
      if (!isNaN(num) && num > 0) return num;
    }
  }

  return null;
}

/**
 * Given a list of entity identifiers and a power map,
 * returns the total STC power in kW for matched components.
 */
export function getTotalStcPowerKw(
  entityIdentifiers: string[],
  powerMap: Map<string, number>
): number | null {
  let total = 0;
  let found = false;

  for (const id of entityIdentifiers) {
    const watts = powerMap.get(id);
    if (watts != null) {
      total += watts;
      found = true;
    }
  }

  return found ? total / 1000 : null;
}
